import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import NavbarUI from "../NavbarUI";
import Sidedrawer from "../SideDrawer";
import styled from "@emotion/styled";
import AlertDialog from "../AlertDialog";
import EndTimeModalContent from "../EndTimeModalContent";
import { useSelector } from "react-redux";
import {
  closeTimeModal,
  elapsedTimeFunc,
  resetAllBodyParts,
  resetAllNotifications,
  resetTimerItemsFunc,
  resetUserFeedNotifications,
  setGoBackFunc,
  setGoNextPatientFunc,
  setIsPaused,
  setIsRunning,
  setIsStopped,
  setLogoutClickedFunc,
  setNavigateItemFunc,
  setPausedTime,
  setPauseStartTime,
  setPreviousPageFunc,
  timeChangedFunc,
} from "../../actions/AppConfig";
import { useDispatch } from "react-redux";
import AlertMessage from "../../common/AlertMessage";
import { logout } from "../../actions/Auth";
import {
  getPatientDetailByPatientId,
  resetAllPatients,
  resetPatientListingStates,
  resetUnpaginatedPatients,
} from "../../actions/Patients";
import { resetStepCounts } from "../../actions/StepsCountActions";
import { resetGetBillingSubscription } from "../../actions/BillingSubscription";
import {
  getAgencyDetailFunc,
  getAgentProfile,
  resetAgentsList,
  resetUnpaginatedAgentsList,
} from "../../actions/Agency";
import dayjs from "dayjs";
import {
  createTimeEntryFunc,
  resetCreateTimeEntry,
} from "../../actions/TimeEntry";
import {
  calculateElapsedTime,
  formatDateForApi,
  formatInstantForApi,
  isFutureLogDateTime,
} from "../../utils/helpers";
import OverlapTimeEntryCard from "../OverlapTimeEntryCard";
import OpenMaximiseIcon from "../../icons/OpenMaximiseIcon";
import { ImportMaximiseIcon } from "../../assets/constants";
import MinimizingImport from "../MinimizeImport";
import { checkImportStatusFunc } from "../../actions/ImportFileActions";
import {
  createCarePlanFunc,
  getCarePlanFunc,
  resetCreateCarePlan,
} from "../../actions/CarePlan";

const StyledBox = styled(Box)(() => ({
  display: "flex",
  overflow: "auto",
  height: "100%",
  // background: "green",
}));

const StyledPage = styled(Box)(() => ({
  width: "100%",
  overflowX: "hidden",
  // padding: "20px 40px",
  // paddingBottom: "40px",
}));

const StyledToolbar = styled(Toolbar)(() => ({
  minHeight: "76px !important",
  height: "76px",
}));

const AppLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const agencyId = Cookies.get("agency_id_vita");

  const [selectedPath, setSelectedPath] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [endTimeError, setEndTimeError] = useState(false);
  const [startFutureTimeError, setStartFutureTimeError] = useState(false);
  const [endFutureTimeError, setEndFutureTimeError] = useState(false);
  const [overlapTimeEntries, setOverlapTimeEntries] = useState([]);

  const intervalRef = useRef(null);

  const patientListingState = useSelector((state) => state.patientListingState);
  const { tab: activeTab, nextPatientObject } = patientListingState;

  const patientDetailByPatientId = useSelector(
    (state) => state.patientDetailByPatientId
  );
  const { patientDetail } = patientDetailByPatientId;

  const getCarePlan = useSelector((state) => state.getCarePlan);
  const { carePlan } = getCarePlan;

  const agentProfile = useSelector((state) => state.agentProfile);
  const { agentUser } = agentProfile;

  const createCarePlan = useSelector((state) => state.createCarePlan);
  const { createdCarePlan } = createCarePlan;

  const loggedInAgent = agentUser?.agent;

  const trackImportPatients = useSelector((state) => state.trackImportPatients);
  const { isMinimizeImport } = trackImportPatients;

  const createTimeEntry = useSelector((state) => state.createTimeEntry);
  const {
    loading: loadingCreateTimeEntry,
    error,
    createdTimeEntry,
  } = createTimeEntry;

  const assignBulkMembers = useSelector((state) => state.assignBulkMembers);
  const { assignedBulkMembers } = assignBulkMembers;

  const checkImportStatus = useSelector((state) => state.checkImportStatus);
  const {
    loading: loadingImportStatus,
    error: errorImportStatus,
    importStatus,
  } = checkImportStatus;

  const getImportId = useSelector((state) => state.getImportId);
  const {
    loading: loadingImportId,
    error: errorImportId,
    importId,
  } = getImportId;

  const timerModal = useSelector((state) => state.timerModal);
  const {
    openModal,
    elapsedTime,
    goBackClicked,
    goNextPatientClicked,
    pausedStartTime,
    pausedTime,
    navigateItem,
    isPaused,
    isStopped,
    logoutClicked,
    startTimeValue,
    endTimeValue,
    logTypeValue,
    notesValue,
    communicationInteraction,
    timeChanged,
    programKeyValue,
  } = timerModal;

  const programOptions =
    patientDetail?.patient?.enrolments
      ?.filter((item) => item?.enrolment_status === "enrolled")
      ?.map((enrolment) => ({
        title: enrolment?.care_program_key,
        value: enrolment?.care_program_key,
        id: enrolment?.id,
      })) || [];

  const location = useLocation();

  const drawerWidth =
    location?.pathname === "/organization-portal" ? "0px" : "253px";

  const saveTimerHandler = () => {
    const today = dayjs();
    const startFuture = isFutureLogDateTime(today, {
      hours: startTimeValue?.format("hh"),
      minutes: startTimeValue?.format("mm"),
      amPm: startTimeValue?.format("A"),
    });
    const endFuture = isFutureLogDateTime(today, {
      hours: endTimeValue?.format("hh"),
      minutes: endTimeValue?.format("mm"),
      amPm: endTimeValue?.format("A"),
    });

    if (startFuture || endFuture) {
      setStartFutureTimeError(startFuture);
      setEndFutureTimeError(endFuture);
      return;
    }

    if (endTimeValue && startTimeValue) {
      const fixedDate = "1970-01-01";
      const normalizedStartTime = dayjs(
        `${fixedDate} ${startTimeValue.format("HH:mm")}`
      );
      const normalizedEndTime = dayjs(
        `${fixedDate} ${endTimeValue.format("HH:mm")}`
      );

      if (normalizedEndTime.isBefore(normalizedStartTime)) {
        setEndTimeError(true);
        return;
      }
    }

    setEndTimeError(false);
    setStartFutureTimeError(false);
    setEndFutureTimeError(false);

    // const fixedDate = "1970-01-01";
    // const start = dayjs(`${dayjs(startTimeValue).format("HH:mm")}`)
    //   .set("second", 0)
    //   .set("millisecond", 0);
    // const end = dayjs(`${dayjs(endTimeValue).format("HH:mm")}`)
    //   .set("second", 0)
    //   .set("millisecond", 0);

    const timeEntryObj = {
      patient_id: patientDetail?.patient?.id,
      agency_id: agencyId,
      date: formatDateForApi(dayjs()),
      start_time: formatInstantForApi(startTimeValue),
      end_time: formatInstantForApi(endTimeValue),
      time_in_seconds: timeChanged
        ? calculateElapsedTime(startTimeValue, endTimeValue)
        : Math.floor(elapsedTime / 1000),
      category: logTypeValue,
      notes: notesValue,
      contains_interaction: communicationInteraction,
      enrolment_id: programKeyValue?.id,
      program_key: programKeyValue?.value,
    };

    dispatch(createTimeEntryFunc(timeEntryObj));
  };

  useEffect(() => {
    dispatch(getAgentProfile());
    dispatch(getAgencyDetailFunc());
  }, []);

  useEffect(() => {
    if (assignedBulkMembers?.ok) {
      if (
        importStatus?.import_status !== "completed" &&
        importStatus?.import_status !== "in_progress"
      ) {
        dispatch(checkImportStatusFunc(importId));
      }

      if (importStatus?.import_status !== "completed") {
        intervalRef.current = setInterval(() => {
          dispatch(checkImportStatusFunc(importId));
        }, 5000);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [assignedBulkMembers, importStatus]);

  useEffect(() => {
    if (createdTimeEntry?.ok) {
      setEndTimeError(false);
      setStartFutureTimeError(false);
      setEndFutureTimeError(false);
      // dispatch(
      //   getTimeEntryFunc(
      //     patientId,
      //     dayjs().subtract(31, "day").format("YYYY-MM-DD"),
      //     dayjs().format("YYYY-MM-DD")
      //   )
      // );
      dispatch(resetCreateTimeEntry());
      dispatch(resetTimerItemsFunc());
      dispatch(setIsRunning(false));
      dispatch(setIsPaused(false));
      dispatch(setIsStopped(true));
      dispatch(timeChangedFunc(false));

      // dispatch(getPatientDetailByPatientId(patientId));
      // const pauseDuration = Date.now() - pausedStartTime;
      // dispatch(setPausedTime(pausedTime + pauseDuration));
      // dispatch(setPauseStartTime(null));
      dispatch(elapsedTimeFunc(0));
      dispatch(closeTimeModal());
      // setShowSuccessMessage(true);
      // dispatch(setPreviousPageFunc(true));
      if (navigateItem === "/") {
        navigate("/");
        setSelectedPath(navigateItem);
      }
      if (navigateItem === `/patients?tab=${activeTab}`) {
        navigate(`/patients?tab=${activeTab}`);
        dispatch(resetStepCounts());
        setSelectedPath(navigateItem);
      }

      if (
        navigateItem ===
        `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
      ) {
        if (!patientDetail?.patient?.care_plan) {
          const carePlanObj = {
            patient_id: patientDetail?.patient?.id,
            created_by: loggedInAgent?.id,
            created_by_name: loggedInAgent?.name,
          };

          dispatch(createCarePlanFunc(carePlanObj));
          // navigate(
          //   `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
          // );
        } else {
          navigate(
            `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
          );
          return;
        }
      }

      if (navigateItem === "/settings?tab=general") {
        navigate("/settings?tab=general");
        setSelectedPath(navigateItem);
      }
      if (navigateItem === "/profile") {
        navigate("/profile");
      }
      if (navigateItem === "/reports") {
        navigate("/reports");
      }

      if (navigateItem === "/import-export-wizard") {
        navigate("/import-export-wizard");
      }
      if (navigateItem === "/practice-terms") {
        navigate("/practice-terms");
        setSelectedPath(navigateItem);
      }
      if (navigateItem === "/seeding-entry") {
        navigate("/seeding-entry");
        setSelectedPath(navigateItem);
      }

      if (goBackClicked) {
        navigate(`/patients?tab=${activeTab}`);
        dispatch(resetStepCounts());
        return;
      }
      if (goNextPatientClicked) {
        navigate(`/patient/${nextPatientObject?.id}?tab=overview`);
        dispatch(resetStepCounts());
        return;
      }
    } else if (error?.message) {
      setOverlapTimeEntries(error?.overlapEntries);
      if (isPaused) {
        dispatch(setIsRunning(false));
        dispatch(setIsPaused(true));
        dispatch(setIsStopped(false));
      }

      setShowErrorMessage(true);
    }
  }, [createdTimeEntry, error]);

  const resetTimerHandler = () => {
    dispatch(setIsRunning(false));
    dispatch(resetStepCounts());
    dispatch(resetTimerItemsFunc());
    dispatch(setIsPaused(false));
    dispatch(setIsStopped(true));
    dispatch(setPreviousPageFunc(false));
    dispatch(timeChangedFunc(false));
    dispatch(elapsedTimeFunc(0));
    dispatch(closeTimeModal());
    setShowTimeEntryModal(false);
    dispatch(resetCreateTimeEntry());

    if (goBackClicked) {
      navigate(`/patients?tab=${activeTab}`);
      dispatch(setGoBackFunc(false));
      return;
    }
    if (goNextPatientClicked) {
      navigate(`/patient/${nextPatientObject?.id}?tab=overview`);
      dispatch(setGoNextPatientFunc(false));
      return;
    }

    if (
      navigateItem ===
      `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
    ) {
      if (!patientDetail?.patient?.care_plan) {
        const carePlanObj = {
          patient_id: patientDetail?.patient?.id,
          created_by: loggedInAgent?.id,
          created_by_name: loggedInAgent?.name,
        };

        dispatch(createCarePlanFunc(carePlanObj));
        // navigate(
        //   `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
        // );
      } else {
        navigate(
          `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
        );
        return;
      }
    }

    if (navigateItem === "/") {
      navigate("/");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/reports") {
      navigate("/reports");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/import-export-wizard") {
      navigate("/import-export-wizard");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/profile") {
      navigate("/profile");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === `/patients?tab=${activeTab}`) {
      navigate(`/patients?tab=${activeTab}`);
      setSelectedPath(navigateItem);
      dispatch(resetPatientListingStates());
      return;
    }

    if (navigateItem === "/billing-report") {
      navigate("/billing-report");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/settings?tab=general") {
      navigate("/settings?tab=general");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/practice-terms") {
      navigate("/practice-terms");
      setSelectedPath(navigateItem);
      return;
    }

    if (navigateItem === "/seeding-entry") {
      navigate("/seeding-entry");
      setSelectedPath(navigateItem);
      return;
    }

    if (logoutClicked) {
      dispatch(logout());
      dispatch(resetUserFeedNotifications());
      dispatch(resetAllNotifications());
      dispatch(resetAllPatients());
      dispatch(resetAllBodyParts());
      dispatch(resetUnpaginatedPatients());
      dispatch(resetAgentsList());
      dispatch(resetUnpaginatedAgentsList());
      dispatch(resetGetBillingSubscription());
      dispatch(resetStepCounts());
      dispatch(resetTimerItemsFunc());
      return;
    }
  };

  const showDiscardTimeEntryModalHandler = () => {
    setShowTimeEntryModal(true);
  };

  // console.log("CREATED CARE PLAN LAYOUT", createdCarePlan);

  useEffect(() => {
    if (createdCarePlan?.ok) {
      dispatch(getCarePlanFunc(patientDetail?.patient?.id));
      // dispatch(getPatientDetailByPatientId(patientDetail?.patient?.id));
      dispatch(resetCreateCarePlan());
      navigate(
        `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
      );
    }
  }, [createdCarePlan]);

  // useEffect(() => {
  //   if (carePlan?.ok) {
  //     navigate(
  //       `/patient/${patientDetail?.patient?.id}/care-plan-workspace/assessment`
  //     );
  //   }
  // }, [carePlan]);

  // console.log("GET CARE PLAN", carePlan);

  const isSaveDisabled =
    (timeChanged &&
      startTimeValue?.format("HH:mm A") === endTimeValue?.format("HH:mm A")) ||
    !programKeyValue?.value ||
    endTimeError ||
    startFutureTimeError ||
    endFutureTimeError;

  return (
    <>
      {/* OPEN STOP TIME MODAL */}
      {isMinimizeImport && <MinimizingImport />}

      {openModal && (
        <AlertDialog
          data={{
            title:
              (goBackClicked ||
                goNextPatientClicked ||
                logoutClicked ||
                navigateItem) &&
              !isStopped
                ? "Save session before you leave"
                : "End Session",
          }}
          disabled={isSaveDisabled}
          openModal={openModal}
          isLoading={loadingCreateTimeEntry}
          showCancelConfirmationModalHandler={showDiscardTimeEntryModalHandler}
          closeModal={() => {
            setEndTimeError(false);
            setStartFutureTimeError(false);
            setEndFutureTimeError(false);
            dispatch(timeChangedFunc(false));
            // if (goBackTimer) {
            // navigate("/patients");
            //   setIsRunning(false);
            //   setIsPaused(false);
            // } else {
            //   setIsRunning(true);
            //   setIsPaused(false);
            //   window.history.pushState({ showModal: false }, "");
            // dispatch(setIsRunning(true));
            if (!isPaused) {
              dispatch(setIsRunning(true));
              const pauseDuration = Date.now() - pausedStartTime;
              dispatch(setPausedTime(pausedTime + pauseDuration));
              dispatch(setPauseStartTime(null));
            } else {
              dispatch(setIsPaused(true));
            }
            dispatch(closeTimeModal());
            dispatch(setGoBackFunc(false));
            dispatch(setLogoutClickedFunc(false));
            dispatch(setNavigateItemFunc(null));

            // }
          }}
          moveFileDialog={true}
          formSubmitFunction={saveTimerHandler}
          submitText="Save"
          cancelText={
            goBackClicked || goNextPatientClicked
              ? "Discard and Leave"
              : "Discard"
          }
          maxWidth="444px"
          titlePadding="16px 24px 0 24px !important"
          titlePaddingTop="16px"
          closeOverlay={false}
          showCancelIcon={true}
          showConfirmationOnCancel={true}
        >
          <EndTimeModalContent
            setEndTimeError={setEndTimeError}
            endTimeError={endTimeError}
            startFutureTimeError={startFutureTimeError}
            endFutureTimeError={endFutureTimeError}
            setStartFutureTimeError={setStartFutureTimeError}
            setEndFutureTimeError={setEndFutureTimeError}
            openModal={openModal}
            programOptions={programOptions}
          />
        </AlertDialog>
      )}

      {showSuccessMessage && (
        <AlertMessage
          message="Time log added successfully"
          severity="success"
          open={showSuccessMessage}
          handleClose={() => setShowSuccessMessage(false)}
        />
      )}

      {showErrorMessage && (
        <AlertMessage
          width="682px"
          severity="error"
          open={true}
          autoHideDurationTime={60000}
          handleClose={() => {
            setShowErrorMessage(false);
            setOverlapTimeEntries([]);
            dispatch(resetCreateTimeEntry());
          }}
          textAlign="normal"
        >
          <Stack gap="14px">
            <Stack>
              <Typography variant="body1" fontWeight={500} color="#5F2120">
                Session can't be saved
              </Typography>
              <Typography variant="body2" color="#5F2120">
                {error?.message}{" "}
              </Typography>
            </Stack>
            <Stack
              gap="8px"
              height={overlapTimeEntries?.length <= 3 ? "auto" : "190px"}
              overflow="auto"
            >
              {overlapTimeEntries?.map((item, index) => (
                <OverlapTimeEntryCard
                  key={index}
                  heading={item?.category}
                  notesValue={item?.notes}
                  startTime={item?.start_time}
                  endTime={item?.end_time}
                  date={item?.date}
                />
              ))}
            </Stack>
          </Stack>{" "}
        </AlertMessage>
      )}

      {showTimeEntryModal && (
        <AlertDialog
          submitID="confirm-delete-time-entry"
          maxWidth="400px"
          openModal={showTimeEntryModal}
          closeModal={() => setShowTimeEntryModal(false)}
          data={{
            title: "Are you sure you want to discard this time entry?",
          }}
          formSubmitFunction={resetTimerHandler}
          color="error"
          fontWeight="400"
        />
      )}

      <StyledBox>
        {/* APP BAR */}
        <NavbarUI drawerWidth={drawerWidth} />

        {/* SIDE DRAWER */}
        {location?.pathname !== "/organization-portal" && (
          <Sidedrawer
            drawerWidth={drawerWidth}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
          />
        )}

        <StyledPage>
          <StyledToolbar />
          <Box
            height="calc(100vh - 76px)"
            overflow="auto"
            sx={{ minHeight: 0 }}
          >
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </Box>
        </StyledPage>
      </StyledBox>
    </>
  );
};

export default AppLayout;
